import { Suspense, useCallback, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { useOutletContext } from "react-router"
import { useLens } from "@hookform/lenses"
import { useFieldArray } from "@hookform/lenses/rhf"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { Banner } from "@/components/ui/banner"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContentCustom, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { CustomSofiaEvent, sofiaEventDispatch } from "@/hooks/useSofiaEventListener"
import { joinStrings } from "@/lib/joinString"
import {
  EpisodeOfCareRelatedConditionschema,
  episodeOfCareRelatedConditionsFormSchema,
  LinkedConditionInformation,
  RelatedConditionsForm,
} from "@/modules/patients/detail/episode-of-care/components/related-conditions-form"
import { TreatmentPlanSuggestion } from "@/modules/patients/detail/episode-of-care/components/treatment-plan-suggestion"
import {
  EpisodeOfCareIcdCodeForm,
  episodeOfCareIcdCodeFormSchema,
} from "@/modules/patients/detail/episode-of-care/episode-of-care-icd-code-form"
import { handleAppendRelatedConditions } from "@/modules/patients/detail/episode-of-care/lib/handle-append-related-conditions"
import { handleUpdateAllPatientCondition } from "@/modules/patients/detail/episode-of-care/lib/handle-update-all-patient-condition"
import { hasMedicationSuggestionForDiagnosisCode } from "@/modules/patients/detail/episode-of-care/services/ch-has-medication-suggestion-for-diagnosis-code"
import { appendTemplateValue } from "@/modules/patients/detail/progress-note/components/progress-note-editable/treatment-plan-section"
import { useProgressNoteFormContext } from "@/modules/patients/detail/progress-note/hooks/useProgressNoteFormStore"
import {
  useEpisodeOfCareStoreContext,
  useGetExcludedIcdCodes,
} from "@/modules/patients/detail/store/useEpisodeOfCareStore"
import { useContextAppointment } from "@/modules/patients/hooks/useContextAppointment"
import { useFetchIcdCodesQuery } from "@/modules/patients/hooks/useIcdDiagnosisQuery"
import { useTreatmentPlanTemplatesData } from "@/modules/patients/hooks/useTreatmentPlanTemplatesData"
import { IcdDiagnosisFragment$data } from "@/modules/patients/services/__generated__/IcdDiagnosisFragment.graphql"
import {
  PatientConditionInformationOutputFragment$data,
  PatientConditionInformationOutputFragment$key,
} from "@/modules/patients/services/__generated__/PatientConditionInformationOutputFragment.graphql"
import { PatientFragment$data } from "@/modules/patients/services/__generated__/PatientFragment.graphql"
import { createOrReuseEpisodeOfCare } from "@/modules/patients/services/createOrReuseEpisodeOfCare"
import { PatientConditionInformationOutputFragment } from "@/modules/patients/services/PatientConditionInformationOutputFragment"
import { usePatientCondition } from "@/modules/patients/services/usePatientCondition"
import { useUpdateAllPatientConditionsMutation } from "@/modules/patients/services/useUpdateAllPatientConditionsMutation"
import { useUpdateProgressNoteEpisodeOfCareMutation } from "@/modules/patients/services/useUpdateProgressNoteEpisodeOfCareMutation"
import { Country } from "@/types/__generated__/union-types"

const createEpisodeOfCareFormSchema = episodeOfCareRelatedConditionsFormSchema.extend(
  episodeOfCareIcdCodeFormSchema.shape,
)

type CreateEpisodeOfCareSchema = z.infer<typeof createEpisodeOfCareFormSchema>

interface CreateEpisodeOfCareDialogProps {
  country: Country
  patientId: string
}

const getDefaultRelatedConditionsFormData = (
  patientConditions: PatientConditionInformationOutputFragment$data,
): EpisodeOfCareRelatedConditionschema["relatedConditions"] => {
  const res = patientConditions.conditions.map((v) => {
    return {
      icdCode: v.icdDiagnosis.diagnosisCode,
      description: v.icdDiagnosis.text,
      value: false,
      isFromIcdSelectInThisSession: false,
    }
  })

  return res.sort((a, b) => a.icdCode.localeCompare(b.icdCode))
}

const CreateEpisodeOfCareDialog = ({ country, patientId }: CreateEpisodeOfCareDialogProps) => {
  const { t } = useTranslation()
  const { patient } = useOutletContext<{ patient: PatientFragment$data }>()
  const [linkedConditionInformation, setLinkedConditionInformation] = useState<LinkedConditionInformation | undefined>()
  const [hasAutoFocusOpened, setHasAutoFocusOpened] = useState(false)
  const [hasMedicationSuggestion, setHasMedicationSuggestion] = useState(false)

  const handleUpdateAllCondition = useUpdateAllPatientConditionsMutation(country)

  const dataRef = usePatientCondition(patientId, country)
  const patientConditionData = useFragment<PatientConditionInformationOutputFragment$key>(
    PatientConditionInformationOutputFragment,
    dataRef,
  )
  const progressNoteForms = useProgressNoteFormContext((state) => state.progressNoteForms)

  const setProgressNoteId = useEpisodeOfCareStoreContext((state) => state.setProgressNoteId)
  const progressNoteId = useEpisodeOfCareStoreContext((state) => state.progressNoteId)
  const assignedEpisodeOfCareIdForTheCurrentProgressNote = useEpisodeOfCareStoreContext(
    (state) => state.assignedEpisodeOfCareIdForTheCurrentProgressNote,
  )

  const assignEpisodeOfCareForm = useEpisodeOfCareStoreContext((state) => state.assignEpisodeOfCareForm)
  const contextAppointment = useContextAppointment()
  const treatmentPlanTemplates = useTreatmentPlanTemplatesData(contextAppointment?.language ?? "ENGLISH")

  const updateProgressNoteEpisodeOfCare = useUpdateProgressNoteEpisodeOfCareMutation(country)

  const setSelectedEpisodeOfCare = useEpisodeOfCareStoreContext((state) => state.setEpisodeOfCareFragmentReference)

  const excludedIcdCodes = useGetExcludedIcdCodes()
  const { icdCodes, debounceSearch, isLoading } = useFetchIcdCodesQuery({
    birthDay: patient.birthDate,
    excludedIcdCodes,
    gender: patient.gender,
    searchType: "EPISODE_OF_CARE",
  })

  const form = useForm<CreateEpisodeOfCareSchema>({
    resolver: zodResolver(createEpisodeOfCareFormSchema),
    defaultValues: {
      diagnosisCode: "",
      relatedConditions: getDefaultRelatedConditionsFormData(patientConditionData),
    },
  })

  const [icdCode, setIcdCode] = useState("")
  const [treatmentPlanFetchKey, setTreatmentPlanFetchKey] = useState(0)
  const [treatmentPlanSuggestions, setTreatmentPlanSuggestionIds] = useState<string[]>([])
  const { handleSubmit, getValues, control } = form

  const lens = useLens({ control })
  const relatedConditionsLen = lens.focus("relatedConditions")

  const { append: appendRelatedCondition, fields: relatedConditionFields } = useFieldArray(
    relatedConditionsLen.interop(),
  )

  const appendTreatmentPlan = useCallback(() => {
    const templates = treatmentPlanTemplates?.filter((treatmentPlan) =>
      treatmentPlanSuggestions.includes(treatmentPlan.id),
    )

    if (!progressNoteId || !templates?.length) return

    const treatmentPlanForm = progressNoteForms.get(progressNoteId)?.treatmentPlanForm
    const setValue = treatmentPlanForm?.setValue
    const currentValues = treatmentPlanForm?.getValues()

    if (!setValue || !currentValues) return

    let { title, recommended = "", followUpInstructions = "" } = currentValues

    templates.forEach((template) => {
      title = joinStrings([title, template.title], ", ")
      recommended = appendTemplateValue(recommended, template.recommendedTreatment)
      followUpInstructions = appendTemplateValue(followUpInstructions, template.followUpInstructions)
    })

    setValue("title", title, {
      shouldDirty: true,
      shouldValidate: true,
    })
    setValue("recommended", recommended, {
      shouldDirty: true,
    })
    setValue("followUpInstructions", followUpInstructions, {
      shouldDirty: true,
    })

    sofiaEventDispatch(CustomSofiaEvent("TREATMENT_PLAN_SUGGESTION_APPEND_EVENT", { progressNoteId }))
  }, [progressNoteForms, progressNoteId, treatmentPlanTemplates, treatmentPlanSuggestions])

  const onSubmit = (data: CreateEpisodeOfCareSchema) => {
    const relatedConditions = data.relatedConditions.filter((e) => e.value).map((e) => e.icdCode)

    createOrReuseEpisodeOfCare(
      country,
      {
        diagnosisCode: data.diagnosisCode,
        patientId,
        relatedConditions,
      },
      (episodeOfCareRes) => {
        if (!episodeOfCareRes) return
        const { id: episodeOfCareId } = episodeOfCareRes
        updateProgressNoteEpisodeOfCare(
          { id: progressNoteId!, episodeOfCareId },
          assignedEpisodeOfCareIdForTheCurrentProgressNote,
          () => {
            setSelectedEpisodeOfCare(episodeOfCareRes)
          },
        )

        handleUpdateAllPatientCondition(
          patientId,
          patientConditionData,
          data.relatedConditions,
          handleUpdateAllCondition,
        )
      },
    )
    assignEpisodeOfCareForm?.("icdCode", data.diagnosisCode, { shouldValidate: true })
    setProgressNoteId(null)
    appendTreatmentPlan()
  }

  const appendRelatedConditions = useCallback(
    (option: IcdDiagnosisFragment$data) => {
      const relatedConditions = getValues("relatedConditions")

      handleAppendRelatedConditions(option, relatedConditions, setLinkedConditionInformation, appendRelatedCondition)
    },
    [getValues, appendRelatedCondition],
  )

  const checkDiagnosisCodeRelatedMedications = async (icdCode: string) => {
    const result = await hasMedicationSuggestionForDiagnosisCode(icdCode)
    setHasMedicationSuggestion(result)
  }

  return (
    <Dialog
      open
      onOpenChange={() => {
        FaroInsightsWrapper.pushEvent("create_episode_of_care_close")
        setProgressNoteId(null)
      }}>
      <DialogContentCustom
        variant="small"
        onPointerDownOutside={(e) => e.preventDefault()}
        onOpenAutoFocus={(e) => {
          e.preventDefault()
          setHasAutoFocusOpened(true)
        }}
        data-testid="new-episode-of-care-dialog">
        <DialogHeader>
          <DialogTitle>{t("episodeOfCare.progressNote.newEpisodeOfCare")}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 px-6 py-2">
          <FormProvider {...form}>
            <div className="relative">
              <EpisodeOfCareIcdCodeForm
                autoFocus={hasAutoFocusOpened}
                onFocusInput={() => {
                  FaroInsightsWrapper.pushEvent("create_episode_of_care_search")
                }}
                options={icdCodes ?? { commons: [], others: [] }}
                onChange={debounceSearch}
                isFetchingIcdCodes={isLoading}
                onOptionSelect={(option) => {
                  checkDiagnosisCodeRelatedMedications(option.diagnosisCode)
                  setIcdCode(option.diagnosisCode)
                  setTreatmentPlanFetchKey((prev) => prev + 1)
                  setTreatmentPlanSuggestionIds([])
                  appendRelatedConditions(option)
                }}
                onClear={() => {
                  setHasMedicationSuggestion(false)
                }}
              />
            </div>
            <Suspense fallback={null}>
              {icdCode && (
                <TreatmentPlanSuggestion
                  key={treatmentPlanFetchKey}
                  country={country}
                  icdCode={icdCode}
                  onChange={(value) => {
                    setTreatmentPlanSuggestionIds((prev) =>
                      prev.includes(value) ? prev.filter((id) => id !== value) : [...prev, value],
                    )
                  }}
                />
              )}
            </Suspense>
            {hasMedicationSuggestion && (
              <Banner type="inline" variant="info">
                {t("patient.healthInformation.medication.suggestedPrescription")}
              </Banner>
            )}
            <RelatedConditionsForm
              lens={relatedConditionsLen}
              fields={relatedConditionFields}
              linkedConditionInformation={linkedConditionInformation}
            />
          </FormProvider>
        </div>
        <DialogFooter className="flex p-6 pt-4">
          <Button
            className="px-4"
            data-testid="episode-of-care-cancel-btn"
            variant="secondary"
            onClick={() => {
              FaroInsightsWrapper.pushEvent("create_episode_of_care_close")
              setProgressNoteId(null)
            }}>
            {t("common.button.cancel")}
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              FaroInsightsWrapper.pushEvent("create_episode_of_care_save")
              handleSubmit(onSubmit)()
            }}
            className="px-4"
            data-testid="episode-of-care-save-btn">
            {t("common.button.save")}
          </Button>
        </DialogFooter>
      </DialogContentCustom>
    </Dialog>
  )
}

export { CreateEpisodeOfCareDialog }
